import React, { useState, useContext, useCallback, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  ModalHeader,
  ModalFooter,
  HStack,
  Button,
  Text,
  Icon,
  Spinner,
  useToast,
  VStack,
  IconButton,
} from "@chakra-ui/react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import { FormProvider, useForm } from "react-hook-form";
import { algoliasearch, SearchClient } from "algoliasearch";
import actions from "src/actions";
import { AlgoliaIndexType } from "common/interfaces/Algolia";
import {
  createDialogContext,
  DialogProvider,
  useDialogWithOptions,
} from "src/Common/Dialog";
import { LookalikeIcon, SocialIcon } from "../AudienceIcons";
import { BasicInfoStep, LookalikeStep } from "./steps";
import { CreateCustomAudienceFormValues, StepConfig } from "./types";
import { CloseIcon } from "@chakra-ui/icons";

export const CreateCustomAudienceDialogContext = createDialogContext();

const CreateCustomAudienceDialog = ({
  isOpen,
  close,
  onCreate = () => {},
}: {
  onCreate?: (audience: any) => void;
  isOpen: boolean;
  close: () => void;
}) => {
  const toast = useToast();
  const form = useForm<CreateCustomAudienceFormValues>({
    defaultValues: {
      name: "",
      genres: [],
      artists: [],
      createLookalike: false,
      type: "kyd",
      lookalike: {
        name: "",
        ratio: undefined,
      },
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const [eventsAlgoliaConfig, setEventsAlgoliaConfig] = useState<{
    indexName: string;
    searchClient: SearchClient;
  } | null>(null);

  // Step configuration
  const steps: StepConfig[] = [
    {
      id: 1,
      title: "Create Custom Audience",
      subtitle: "Use any of the filters below to build your audience",
      component: BasicInfoStep,
      nextButtonText: "Next",
      canProceed: (form) => !!form.watch("name"),
    },
    {
      id: 2,
      title: "Create lookalike audience",
      subtitle:
        "Create a lookalike audience based on your custom audience to reach similar people",
      component: LookalikeStep,
      nextButtonText: "Create Audience",
      canProceed: (form) => {
        return true;
      },
    },
  ];

  const currentStep = steps[currentStepIndex];

  const fetchEventsAlgoliaConfig = useCallback(async () => {
    const response = await actions.ADMIN.fetchAlgoliaConfig({
      indexType: AlgoliaIndexType.EVENTS,
    });
    if (response.events_config) {
      const search = algoliasearch(
        response.events_config.app_id,
        response.events_config.api_key
      );
      search.clearCache();
      setEventsAlgoliaConfig({
        indexName: response.events_config.index_name,
        searchClient: search,
      });
    }
  }, []);

  const handleCancel = () => {
    if (currentStepIndex === 0) {
      close();
    } else {
      setCurrentStepIndex(0);
    }
  };

  const handleNext = useCallback(async () => {
    setIsLoading(true);
    try {
      if (currentStepIndex < steps.length - 1) {
        setCurrentStepIndex(currentStepIndex + 1);
      }
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description: "Unknown error",
        status: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [currentStepIndex, steps.length, toast]);

  const handleSubmit = useCallback(
    async (data: CreateCustomAudienceFormValues) => {
      setIsLoading(true);
      try {
        onCreate(data);

        toast({
          title: "Success",
          description: "Custom audience created successfully",
          status: "success",
        });
        close();
        form.reset();
        setCurrentStepIndex(0);
      } catch (error) {
        console.error("Error creating audience:", error);
        toast({
          title: "Error",
          description: "Failed to create audience",
          status: "error",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [form, toast, close]
  );

  const handleSave = () => {
    if (currentStepIndex < steps.length - 1) {
      form.handleSubmit(handleNext)();
    } else {
      form.handleSubmit(handleSubmit)();
    }
  };

  useEffect(() => {
    fetchEventsAlgoliaConfig();
  }, [fetchEventsAlgoliaConfig]);

  const lookalikeOptions = [
    {
      title: "Create 0-1% lookalike",
      description: "",
      value: "0-1",
      icon: <SocialIcon />,
      iconBgColor: "#DFEAFD",
      isRecommended: true,
    },
    {
      title: "Create 0-3% lookalike",
      description: "",
      value: "0-3",
      icon: <SocialIcon />,
      iconBgColor: "#FDF5DF",
      isRecommended: false,
    },
    {
      title: "Create 0-5% lookalike",
      description: "",
      value: "0-5",
      icon: <SocialIcon />,
      iconBgColor: "#FDE3DF",
      isRecommended: false,
    },
  ];

  const StepComponent = currentStep.component;

  return (
    <Modal isOpen={isOpen} onClose={close} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent bg="white" borderRadius="lg" p={0} minH="40vh">
        {eventsAlgoliaConfig ? (
          <FormProvider {...form}>
            <Box px={6} id="custom-audience-form">
              <ModalHeader px={0}>
                <HStack justify="space-between">
                  <Box>
                    <Text fontSize="lg" fontWeight="semibold">
                      {currentStep.title}
                    </Text>
                    <Text fontSize="sm" color="gray.600" fontWeight="medium">
                      {currentStep.subtitle}
                    </Text>
                  </Box>
                  <IconButton
                    aria-label="Close"
                    icon={<CloseIcon color="gray.500" fontSize={12} />}
                    onClick={close}
                    variant="ghost"
                    color="gray.400"
                    size="xs"
                  />
                </HStack>
              </ModalHeader>

              <StepComponent
                eventsAlgoliaConfig={eventsAlgoliaConfig}
                lookalikeOptions={lookalikeOptions}
              />
              <HStack justify="flex-end" spacing={3} mt={4}>
                <Button
                  onClick={handleSave}
                  bgColor={
                    currentStep.nextButtonText === "Next" ? "gray.900" : "green"
                  }
                  color="white"
                  _hover={{
                    bgColor:
                      currentStep.nextButtonText === "Next"
                        ? "black"
                        : "green.600",
                  }}
                  isDisabled={!currentStep.canProceed(form)}
                  rounded="lg"
                  fontSize="16px"
                  fontWeight="semibold"
                  isLoading={isLoading}
                  rightIcon={
                    currentStep.nextButtonText === "Next" ? (
                      <Icon as={FiArrowRight} ml={2} />
                    ) : undefined
                  }
                >
                  {currentStep.nextButtonText}
                </Button>
              </HStack>
            </Box>
          </FormProvider>
        ) : (
          <VStack
            h="full"
            w="full"
            justifyContent="center"
            alignItems="center"
            flexGrow={1}
          >
            <Spinner />
          </VStack>
        )}
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};

const CreateCustomAudienceDialogWrapper = ({
  onCreate,
}: {
  onCreate: (audience: any) => void;
}) => {
  const { isOpen, close } = useDialogWithOptions(
    CreateCustomAudienceDialogContext
  );

  return (
    <CreateCustomAudienceDialog
      onCreate={onCreate}
      isOpen={isOpen}
      close={close}
    />
  );
};

export const withCreateCustomAudienceDialog =
  <P extends object>(Component: React.ComponentType<P>) =>
  (props: P & { onChange?: (audience: any) => void }) => {
    return (
      <DialogProvider context={CreateCustomAudienceDialogContext}>
        <Component {...props} />
        <CreateCustomAudienceDialogWrapper
          onCreate={props.onChange || (() => {})}
        />
      </DialogProvider>
    );
  };

export default CreateCustomAudienceDialog;
